import { useCallback, useEffect, useState } from "react";

import clipboardCopy from "clipboard-copy";

import {
  Edit as EditIcon,
  Link as LinkIcon,
  Payments as PaymentsIcon,
} from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/features/authentication";
import {
  AddOutletFormDialog,
  EditOutletFormDialog,
  OutletRoleEnum,
  outletRoleLabels,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const Outlets = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [outletId, setOutletId] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadOutlets = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("/outlets", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
          withCompany: true,
        },
      });
      setOutlets(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat cabang toko"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadOutlets();
  }, [loadOutlets]);

  const handleLinkGenerate = (id, name) => {
    const url = `${import.meta.env.VITE_POS_BASE_URL}/outlet-setup?id=${id}`;
    clipboardCopy(url).then(() => {
      showSnackbar(`Link cabang ${name} tersalin ke clipboard`, "success");
    });
  };

  const toolbarContent = hasPermission(PermissionEnum.CREATE_OUTLETS) && (
    <Button variant="contained" onClick={() => setIsAddDialogOpen(true)}>
      Tambah
    </Button>
  );

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama cabang toko"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={outlets}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 2,
              sortable: false,
            },
            {
              field: "role",
              headerName: "Peran",
              flex: 1.5,
              sortable: false,
              valueGetter: (value) => (value ? outletRoleLabels[value] : "-"),
            },
            {
              field: "company",
              headerName: "Perusahaan",
              flex: 1.5,
              sortable: false,
              valueGetter: (value) => value?.name ?? "-",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              minWidth: 200,
              headerName: "Aksi",
              getActions: (params) => {
                const actions = [];
                if (hasPermission(PermissionEnum.UPDATE_OUTLETS)) {
                  actions.push(
                    <IconButton
                      key="edit"
                      onClick={() => setOutletId(params.id)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>,
                  );
                  if (params.row.role === OutletRoleEnum.RETAIL) {
                    actions.push(
                      <IconButton
                        key="payment-methods"
                        onClick={() => navigate(`${params.id}/payment-methods`)}
                        size="small"
                      >
                        <PaymentsIcon />
                      </IconButton>,
                    );
                  }
                }
                actions.push(
                  <IconButton
                    key="link"
                    onClick={() =>
                      handleLinkGenerate(params.id, params.row.name)
                    }
                    size="small"
                  >
                    <LinkIcon />
                  </IconButton>,
                );
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        {isAddDialogOpen && (
          <AddOutletFormDialog
            onClose={() => setIsAddDialogOpen(false)}
            onSubmitted={loadOutlets}
          />
        )}
        {!!outletId && (
          <EditOutletFormDialog
            outletId={outletId}
            onClose={() => setOutletId(null)}
            onSubmitted={loadOutlets}
          />
        )}
      </Stack>
    </PageContainer>
  );
};
